import { query as fbQuery, collection, where, onSnapshot, and, doc, getDoc, orderBy, getDocs, limit, limitToLast, startAfter, collectionGroup, or } from "firebase/firestore";
import { db, rtDb, UseCloudFunction } from "./firebase.utils";
import { child, equalTo, get, getDatabase, onChildAdded, onChildChanged, onChildRemoved, onValue, orderByChild, ref, startAt, query as rtQuery, limitToLast as rtLimitToLast, limitToFirst } from "firebase/database";
import sizeof from "firestore-size";

/**
 * Every read and listener in this file is fire-and-forget: callers hand in a
 * callback and never see a promise. Without this, a rejected read or a dropped
 * listener fails completely silently and the UI waits forever.
 * Logs the failure, then hands it to an optional `e.onError` so the caller can
 * show something instead of spinning.
 */
function handleDbError(context, err, e) {
  console.error(`getDataFromDb: ${context} failed`, err);
  if (!e || !e.onError) return;
  try {
    e.onError(err);
  } catch (callbackErr) {
    console.error(`getDataFromDb: onError callback for ${context} threw`, callbackErr);
  }
}


export async function getRegsFromDb(e) {
  let regsRef;

  if (e.appId && e.status) {
    regsRef = fbQuery(collectionGroup(db, `registrations`), and(where('list', 'array-contains', e.appId), where('status', '==', e.status)));
    // and(where('list', 'array-contains', e.appId), where('status', '==', e.status))
  } else if (e.appId) {
    regsRef = fbQuery(collectionGroup(db, `registrations`), where('list', 'array-contains', e.appId));
  } else if (e.gameId) {
    regsRef = collection(db, `games/${e.gameId}/registrations`);
  }

  if (e.once) {
    const regsSnap = await getDocs(regsRef).catch(err => handleDbError('getRegsFromDb', err, e));
    if (!regsSnap) return;
    let data = {};
    regsSnap.forEach(doc => {
      data[doc.data().gameId] = doc.data();
    });
    e.callback(data);
    return;
  }

  const unsubscribe = onSnapshot(regsRef, docs => {
    docs.forEach(async doc => {
      e.callback(doc.data());
    });
  }, err => handleDbError('getRegsFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getGameDataFromDb(e) {
  let gameRef;

  if (e.gameId && e.once) {
    const gameSnap = await getDoc(doc(db, 'games', e.gameId)).catch(err => handleDbError('getGameDataFromDb', err, e));
    if (!gameSnap) return;
    const gameData = gameSnap.data();
    e.callback(gameData);
    return;
  }
  else if (e.gameId) {
    gameRef = doc(db, 'games', e.gameId);
    const unsubscribe = onSnapshot(gameRef, doc => {
      let gameData = doc.data();
      e.callback(gameData);
    }, err => handleDbError('getGameDataFromDb', err, e))
    e.handleListener(unsubscribe);
    return;
  } else if (e.joinCode && e.status) {
    gameRef = fbQuery(
      collection(db, 'games'),
      and(
        where('joinCode', '==', e.joinCode),
        where('status', '==', e.status)
      )
    )
  } else if (e.gamePath) {
    gameRef = fbQuery(
      collection(db, 'games'),
      where('path', '==', e.gamePath),
    )
  } else if (e.template) {
    gameRef = fbQuery(
      collection(db, 'games'),
      where('template', '==', true)
    )
  }
  // console.log(gameRef);

  if (e.once && e.gameId) {
    const gameSnap = await getDoc(gameRef).catch(err => handleDbError('getGameDataFromDb', err, e));
    if (!gameSnap) return;
    const gameData = gameSnap.data();
    const lastActivity = await getLastActivity(gameData.gameId);
    // console.log(lastActivity);
    if (!lastActivity) {
      gameData.lastActivity = gameData.ts;
    } else {
      gameData.lastActivity = lastActivity.ts;
    }
    if (e.appId) {
      const lastUserActivity = getLastActivity(gameData.gameId, e.appId);
      if (lastUserActivity) {
        gameData.lastUserActivity = lastUserActivity.ts;
      }
    }
    e.callback(gameData);
    return;
  } else if (e.once) {
    const games = await getDocs(gameRef).catch(err => handleDbError('getGameDataFromDb', err, e));
    if (!games) return;
    let gameData = {};
    games.forEach(doc => {
      gameData[doc.id] = doc.data();
    })
    e.callback(gameData);
    return;
  } else {
    const unsubscribe = onSnapshot(gameRef, docs => {
      docs.forEach(async doc => {
        let gameData = doc.data();
        e.callback(gameData);
      });
    }, err => handleDbError('getGameDataFromDb', err, e))
    e.handleListener(unsubscribe);
  }
}

export async function getLastActivity(gameId, appId) {
  let activityRef;
  if (gameId && appId) {
    activityRef = fbQuery(
      collection(db, `games/${gameId}/gameActivity`),
      where('appId', '==', appId),
      orderBy("ts", "desc"),
      limit(1)
    )
  } else if (gameId) {
    activityRef = fbQuery(
      collection(db, `games/${gameId}/gameActivity`),
      orderBy("ts", "desc"),
      limit(1)
    )
  }
  const activitySnap = await getDocs(activityRef).catch(err => handleDbError('getLastActivity', err));
  // console.log(activitySnap.size);
  if (!activitySnap || activitySnap.size === 0) return false;
  let lastActivity;
  activitySnap.forEach(doc => {
    const activityData = doc.data();
    // console.log(activityData);
    lastActivity = activityData;
  })
  return lastActivity;
}

export async function getPlayerDataFromDb(e) {
  let playerRef;
  // console.log(e)

  if (e.appId && e.gameId) {
    playerRef = fbQuery(collection(db, `games/${e.gameId}/players`), where('appId', '==', e.appId));
  } else if (e.gameId && e.enrollMethod) {
    playerRef = fbQuery(collection(db, `games/${e.gameId}/players`), where('enroll_method', '==', e.enrollMethod))
  } else if (e.gameId && e.removed) {
    playerRef = fbQuery(collection(db, `games/${e.gameId}/players`), where('status', '!=', 'active'));
  } else if (e.gameId && e.playerPath) {
    playerRef = fbQuery(collection(db, `games/${e.gameId}/players`), where('path', '==', e.playerPath));
  } else if (e.gameId) {
    playerRef = collection(db, `games/${e.gameId}/players`);
  } else if (e.playerId) {
    playerRef = fbQuery(collection(db, 'players'), where('playerId', '==', e.playerId));
  }

  const unsubscribe = onSnapshot(playerRef, docs => {
    docs.forEach(async doc => {
      let playerData = doc.data();
      e.callback(playerData);
    });
  }, err => handleDbError('getPlayerDataFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getPlayersFromPlayerDocs(e) {
  let playerDocsRef = "";

  if (e.limit) {
    playerDocsRef = rtQuery(ref(rtDb, `games/${e.gameId}/playerDocs`), orderByChild('ts'), rtLimitToLast(e.limit));
  } else {
    playerDocsRef = ref(rtDb, `games/${e.gameId}/playerDocs`);
  }

  get(playerDocsRef).then(snap => {
    if (snap.exists()) {
      const data = snap.val();
      e.callback(data);
    }
  }).catch(err => handleDbError('getPlayersFromPlayerDocs', err, e));

  if (e.once) return;
  const nowObj = await UseCloudFunction('getServerTimeStamp', {})
    .catch(err => handleDbError('getPlayersFromPlayerDocs getServerTimeStamp', err, e));
  if (!nowObj) return;
  const now = nowObj.ts;
  let playerDocsRef2 = "";
  if (e.limit) {
    playerDocsRef2 = rtQuery(ref(rtDb, `games/${e.gameId}/playerDocs`), orderByChild('ts'), startAt(now), rtLimitToLast(e.limit));
  } else {
    playerDocsRef2 = rtQuery(ref(rtDb, `games/${e.gameId}/playerDocs`), orderByChild('ts'), startAt(now));
  }
  onChildAdded(playerDocsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.playerId]: data });
  }, err => handleDbError('getPlayersFromPlayerDocs onChildAdded', err, e))
  onChildChanged(playerDocsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.playerId]: data });
  }, err => handleDbError('getPlayersFromPlayerDocs onChildChanged', err, e))
  e.handleListener(playerDocsRef2);
}

export async function getFilteredDataFromRtDb(e) {
  // let dataRef = ref(rtDb, e.path).orderByChild(e.key).equalTo(e.value);
  let dataRef = rtQuery(ref(rtDb, e.path), orderByChild(e.key), equalTo(e.value));
  onValue(dataRef, snap => {
    const data = snap.val();
    e.callback(data);
  }, err => handleDbError('getFilteredDataFromRtDb', err, e))
  e.handleListener(dataRef);
}

export async function getGameElementsFromRtDb(e) {
  let elementsRef = ref(rtDb, `games/${e.gameId}/gameElementShortList`);
  get(elementsRef).then(snap => {
    if (snap.exists()) {
      const data = snap.val();
      e.callback(data);
    }
  }).catch(err => handleDbError('getGameElementsFromRtDb', err, e));
  const now = new Date().getTime();
  const elementsRef2 = rtQuery(
    ref(rtDb, `games/${e.gameId}/gameElementShortList`),
    orderByChild('ts', 'asc'),
    startAt(now)
  );

  onChildAdded(elementsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.id]: data });
  }, err => handleDbError('getGameElementsFromRtDb onChildAdded', err, e))
  onChildChanged(elementsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.id]: data });
  }, err => handleDbError('getGameElementsFromRtDb onChildChanged', err, e))
  e.handleListener(elementsRef2);
}


export async function getGameElementsFromDb(e) {
  let elementsRef;

  if (e.gameId && e.path) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`),
      where('path', '==', e.path),
    );
  }
  else if (e.gameId && e.status && e.market) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`),
      and(
        where('status', '==', e.status),
        where('opt_market', '==', e.market),
      )
    );
  }
  else if (e.gameId && e.market) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`),
      where('opt_market', '==', e.market),
    );
  }
  else if (e.gameId && e.elementType && e.status) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`),
      and(
        where('type', '==', e.elementType),
        where('status', '==', e.status),
        where('opt_visible', '==', true)
      )
    )
  }
  else if (e.gameId && e.elementType) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`),
      where('type', '==', e.elementType),
    )
  }
  else if (e.gameId) {
    elementsRef = fbQuery(
      collection(db, `games/${e.gameId}/gameElements`)
    )
  }

  if (e.once) {
    const elementSnap = await getDocs(elementsRef).catch(err => handleDbError('getGameElementsFromDb', err, e));
    if (!elementSnap) return;
    let elementsData = {};
    elementSnap.forEach(doc => {
      elementsData[doc.id] = doc.data();
    });
    e.callback(elementsData);
    return;
  }

  const unsubscribe = onSnapshot(elementsRef, docs => {
    docs.forEach(async doc => {
      let elementsData = doc.data();
      e.callback(elementsData);
    });
  }, err => handleDbError('getGameElementsFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getGameElementShortList(e) {
  const elementsRef = collection(db, `games/${e.gameId}/gameElementsShortList`);

  if (e.once) {
    const elementSnap = await getDocs(elementsRef).catch(err => handleDbError('getGameElementShortList', err, e));
    if (!elementSnap) return;
    let elementsData = {};
    elementSnap.forEach(doc => {
      elementsData = {
        ...elementsData,
        ...doc.data()
      }
    });
    e.callback(elementsData);
    return;
  }

  const unsubscribe = onSnapshot(elementsRef, docs => {
    let elementObj = {}
    docs.forEach(async doc => {
      // console.log(doc.data());
      elementObj = {
        ...elementObj,
        ...doc.data()
      }
    });
    e.callback(elementObj);
  }, err => handleDbError('getGameElementShortList', err, e))
  e.handleListener(unsubscribe);
}

//DEPRECATED
export async function getGameElementsFromDbForPlayers_DEP(e) {
  let elementsRef;

  if (e.gameId && e.type && e.status) {
    elementsRef = fbQuery(collection(db, 'gameElements'), and(where('gameId', '==', e.gameId), where('type', '==', e.type), where('status', '==', e.status), where('public', '==', true), where('opt_visible', '==', true)));
  } else if (e.gameId && e.status && e.lastDate) {
    elementsRef = fbQuery(collection(db, 'gameElements'), and(where('gameId', '==', e.gameId), where('status', '==', e.status), where('ts', '>', e.lastDate), where('public', '==', true)));
  } else if (e.gameId && e.status) {
    elementsRef = fbQuery(collection(db, 'gameElements'), and(where('gameId', '==', e.gameId), where('status', '==', e.status), where('public', '==', true), where('opt_visible', '==', true)));
  }

  const unsubscribe = onSnapshot(elementsRef, docs => {
    docs.forEach(async doc => {
      let elementsData = doc.data();
      e.callback(elementsData);
    });
  }, err => handleDbError('getGameElementsFromDbForPlayers_DEP', err, e))
  e.handleListener(unsubscribe);
}

// DEPRECATED
export async function getSpecificGameElementFromDb_DEP(e) {
  let elementRef;

  if (e.path && e.gameId && e.status) {
    elementRef = fbQuery(collection(db, 'gameElements'), and(where('gameId', '==', e.gameId), where('path', '==', e.path), where('status', '==', e.status)));
  }

  const unsubscribe = onSnapshot(elementRef, docs => {
    docs.forEach(async doc => {
      let elementData = doc.data();
      e.callback(elementData);
    });
  }, err => handleDbError('getSpecificGameElementFromDb_DEP', err, e))
  e.handleListener(unsubscribe);
}

export async function getPlayerActivityFromDb(e) {
  let activityRef;

  if (e.gameId && e.playerId && e.display && e.lastActivity && e.limit) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/gameActivity`),
      and(
        where('playerId', '==', e.playerId),
        where('display', '==', e.display),
        where('ts', '<', e.lastActivity)
      ),
      limit(e.limit)

    );
  } else if (e.gameId && e.playerId && e.display && e.limit) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/gameActivity`),
      and(
        where('playerId', '==', e.playerId),
        where('display', '==', e.display)
      ),
      limit(e.limit)
    );
  } else if (e.gameId && e.playerId && e.display) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/gameActivity`),
      and(
        where('playerId', '==', e.playerId),
        where('display', '==', e.display)
      )
    );
  }

  if (!e.handleListener) {
    // getDocs takes no callback — it returns a promise. The second argument here
    // was silently ignored, so this branch never delivered any data.
    const activitySnap = await getDocs(activityRef).catch(err => handleDbError('getPlayerActivityFromDb', err, e));
    if (!activitySnap) return;
    activitySnap.forEach(doc => {
      let activityData = doc.data();
      e.callback(activityData);
    });
  } else {
    const unsubscribe = onSnapshot(activityRef, docs => {
      docs.forEach(async doc => {
        let activityData = doc.data();
        e.callback(activityData);
      });
    }, err => handleDbError('getPlayerActivityFromDb', err, e))
    e.handleListener(unsubscribe);
  }
}

export async function getLatestGameActivity(e) {
  let activityRef;
  if (e.gameId && e.playerId) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      where('playerId', '==', e.playerId),
      orderBy('ts', 'asc'),
      limitToLast(30)
    );
  } else if (e.gameId) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      orderBy('ts', 'asc'),
      limitToLast(30)
    );
  }

  if (e.once) {
    const activitySnap = await getDocs(activityRef).catch(err => handleDbError('getLatestGameActivity', err, e));
    if (!activitySnap) return;
    let activityData = {};
    activitySnap.forEach(doc => {
      activityData[doc.id] = doc.data();
    });
    e.callback(activityData);
    return;
  }

  const unsubscribe = onSnapshot(activityRef, docs => {
    docs.forEach(doc => {
      let activityData = doc.data();
      e.callback(activityData);
    });
  }, err => handleDbError('getLatestGameActivity', err, e))
  e.handleListener(unsubscribe);
}

export async function getActivityRecordsQuery(e) {
  let dataRef;

  let filterArr = [];
  if (e.filter) {
    const keysArray = e.filter.map(obj => obj.key);
    // console.log(keysArray);
    for (let f of keysArray) {
      let comp = e.filter.find(obj => obj.key === f)?.value;
      let condition = e.filter.find(obj => obj.key === f)?.condition;
      // console.log(f, comp);
      if (f !== 'callback' && f !== 'handleListener' && f !== 'once') {
        if (f === 'startDate') {
          filterArr.push(where('ts', '>=', comp));
        } else if (f === 'endDate') {
          filterArr.push(where('ts', '<=', comp));
        } else if (f === 'elementId') {
          filterArr.push(where('rewardIds', 'array-contains', comp));
        } else if (condition) {
          filterArr.push(where(f, condition, comp));
        } else {
          filterArr.push(where(f, '==', comp));
        }
      }
    }
  }
  // console.log(filterArr);
  if (!e.lastEntry && e.doNotPaginate) {
    dataRef = fbQuery(
      collection(db, `games/${e.gameId}/${e.path}`),
      and(...filterArr)
    )
  } else if (e.lastEntry) {
    dataRef = fbQuery(
      collection(db, `games/${e.gameId}/${e.path}`),
      and(...filterArr),
      orderBy('ts', 'desc'),
      startAfter(e.lastEntry.ts),
      limit(e.limit || 10)
    )
  } else {
    dataRef = fbQuery(
      collection(db, `games/${e.gameId}/${e.path}`),
      and(...filterArr),
      orderBy('ts', 'desc'),
      limit(e.limit || 10)
    )
  }
  if (e.once) {
    const docs = await getDocs(dataRef).catch(err => handleDbError('getActivityRecordsQuery', err, e));
    if (!docs) return;
    let docObj = {}
    docs.forEach((doc) => {
      docObj[doc.id] = doc.data();
    });
    e.callback(docObj);
    return;
  }

  const unsubscribe = onSnapshot(dataRef, docs => {
    docs.forEach(doc => {
      e.callback(doc.data(), doc.id);
    })
  }, err => handleDbError('getActivityRecordsQuery', err, e))
  e.handleListener(unsubscribe);
}

// DEPRECATED
export async function getLatestGameActivityFromRtDb(e) {
  let activityRef;
  if (e.gameId && e.playerId) {
    activityRef = rtQuery(ref(rtDb, `gameActivityRecords/${e.gameId}`), orderByChild('playerId'), equalTo(e.playerId), rtLimitToLast(50));
  } else if (e.gameId) {
    activityRef = rtQuery(ref(rtDb, `gameActivityRecords/${e.gameId}`), orderByChild('ts'), rtLimitToLast(50));
  }

  if (e.once) {
    get(activityRef).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        if (e.callback) {
          e.callback(data);
        }

        if (e.handleListener) {
          e.handleListener(activityRef);
        }
      } else {
        if (e.callback) {
          e.callback(null);
        }

        if (e.handleListener) {
          e.handleListener(activityRef);
        }
      }
    }).catch(err => handleDbError('getLatestGameActivityFromRtDb', err, e))
  }

  onChildAdded(activityRef, snap => {
    const data = snap.val();
    if (e.callback) {
      e.callback({ [data.id]: data });
    }
    if (e.handleListener) {
      e.handleListener(activityRef);
    }
  }, err => handleDbError('getLatestGameActivityFromRtDb onChildAdded', err, e))
}

// DEPRECATED
export async function getGameActivityFromRtDb_DEP(e) {
  let activityRef;
  if (e.playerId && e.gameId) {
    activityRef = rtQuery(ref(rtDb, `games/${e.gameId}/playerRewards`), orderByChild('playerId'), equalTo(e.playerId));
    onChildAdded(activityRef, snap => {
      const data = snap.val();
      e.callback(data);
    }, err => handleDbError('getGameActivityFromRtDb_DEP onChildAdded', err, e))
    e.handleListener(activityRef);
    return;
  } else {
    activityRef = ref(rtDb, `gameActivityRecords/${e.gameId}`);
    get(activityRef).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        e.callback(data);
      }
    }).catch(err => handleDbError('getGameActivityFromRtDb_DEP', err, e));
    const now = new Date().getTime();
    let activityRef2 = rtQuery(ref(rtDb, `gameActivityRecords/${e.gameId}`), orderByChild('ts'), startAt(now));
    onChildAdded(activityRef, snap => {
      const data = snap.val();
      e.callback({ [data.id]: data });
    }, err => handleDbError('getGameActivityFromRtDb_DEP onChildAdded', err, e))
    e.handleListener([activityRef, activityRef2]);
    return;
  }
}

export async function getGameActivityFromDb(e) {
  let activityRef;
  // console.log(e);
  if (e.gameId && e.activityId) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      where('id', '==', e.activityId)
    );
  }
  else if (e.gameId && e.queryArr) {
    let filterArr = [];
    for (let q of e.queryArr) {
      filterArr.push(where(q.key, q.condition, q.value));
    }
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      and(...filterArr)
    );
  }
  else if (e.gameId && e.playerId && e.elementId && e.lastTs && e.limit) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      and(
        where('playerId', '==', e.playerId),
        where('rewardIds', 'array-contains', e.elementId),
        where('ts', '<', e.lastTs)
      ),
      orderBy('ts', 'desc'),
      limit(e.limit)
    );
  }

  else if (e.gameId && e.playerId && e.elementId) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      and(
        where('playerId', '==', e.playerId),
        where('rewardIds', 'array-contains', e.elementId)
      )
    );
  }
  else if (e.gameId && e.playerId && e.activityType) {
    activityRef = fbQuery(
      collection(db, `games/${e.gameId}/activityRecords`),
      and(
        where('playerId', '==', e.playerId),
        where('type', '==', e.activityType)
      )
    );
  }
  else if (e.gameId) {
    activityRef = collection(db, `games/${e.gameId}/activityRecords`);
  }

  if (e.once) {
    const activitySnap = await getDocs(activityRef).catch(err => handleDbError('getGameActivityFromDb', err, e));
    if (!activitySnap) return;
    let activityData = {};
    activitySnap.forEach(doc => {
      activityData[doc.id] = doc.data();
    });
    e.callback(activityData);
    return;
  }

  const unsubscribe = onSnapshot(activityRef, docs => {
    // let dates = []
    // let count = 0;
    docs.forEach(doc => {
      let activityData = doc.data();
      e.callback(activityData);
      // if (!e.getAll) { 
      //     dates.push(activityData.ts);
      //     count++
      //     if (count === docs.size && !e.player) {
      //         if (docs.size < 10) {
      //             fillTwenty(dates.sort(), docs);
      //         }
      //     }
      // }
    });
    // if (e.more && docs.size < 10) {
    //     e.callback({'done': {'ts': 0, 'done': true}});
    // }
  }, err => handleDbError('getGameActivityFromDb', err, e))
  if (e.handleListener) {
    e.handleListener(unsubscribe);
  }



  // async function fillTwenty(dates, docs) {
  //     let twentyMoreRef;
  //     if (e.gameId && e.sevenDays) {
  //         const lastDate = dates[0]
  //         twentyMoreRef = fbQuery(collection(db, 'gameActivity'), and(where('gameId', '==', e.gameId), where('ts', '<', lastDate)), limit(10-docs.size));
  //     }

  //     const querySnap = await getDocs(twentyMoreRef);
  //     querySnap.forEach(doc => {
  //         e.callback(doc.data())
  //     })
  // }
}


export async function getActionItemsFromFirebase(e) {
  let actionItemsRef;

  if (e.gameId && e.playerId && e.status) {
    actionItemsRef = fbQuery(
      collection(db, `games/${e.gameId}/actionItems`),
      and(
        where('playerId', '==', e.playerId),
        where('status', '==', e.status)
      )
    );
  } else if (e.gameId && e.playerId && e.elementId) {
    actionItemsRef = fbQuery(
      collection(db, `games/${e.gameId}/actionItems`),
      and(
        where('playerId', '==', e.playerId),
        where('elementId', '==', e.elementId)
      )
    );
  } else if (e.gameId && e.status) {
    actionItemsRef = fbQuery(
      collection(db, `games/${e.gameId}/actionItems`),
      where('status', '==', e.status)
    );
  } else {
    return;
  }

  const unsubscribe = onSnapshot(actionItemsRef, docs => {
    docs.forEach(async doc => {
      let actionItem = doc.data();
      e.callback(actionItem);
    });
  }, err => handleDbError('getActionItemsFromFirebase', err, e))
  e.handleListener(unsubscribe);
}


// DEPRECATED
export async function getActionItemsFromRtDb_DEP(e) {
  let actionItemsRef;

  if (e.gameId && e.playerId) {
    actionItemsRef = rtQuery(ref(rtDb, `gameActionItems/${e.gameId}`), orderByChild('playerId'), equalTo(e.playerId));
    onChildAdded(actionItemsRef, snap => {
      const data = snap.val();
      e.callback(data);
    }, err => handleDbError('getActionItemsFromRtDb_DEP onChildAdded', err, e))
    onChildChanged(actionItemsRef, snap => {
      const data = snap.val();
      e.callback(data);
    }, err => handleDbError('getActionItemsFromRtDb_DEP onChildChanged', err, e))
    e.handleListener(actionItemsRef);
    return;
  } else if (e.gameId) {
    actionItemsRef = ref(rtDb, `gameActionItems/${e.gameId}`);
    get(actionItemsRef).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        e.callback(data);
      }
    }).catch(err => handleDbError('getActionItemsFromRtDb_DEP', err, e));
    onChildChanged(actionItemsRef, snap => {
      const data = snap.val();
      // console.log('changed');
      // console.log(data);
      e.callback({ [data.id]: data });
    }, err => handleDbError('getActionItemsFromRtDb_DEP onChildChanged', err, e))
    const now = new Date().getTime();
    let actionItemsRef2 = rtQuery(ref(rtDb, `gameActionItems/${e.gameId}`), orderByChild('ts'), startAt(now));
    onChildAdded(actionItemsRef2, snap => {
      const data = snap.val();
      e.callback({ [data.id]: data });
    }, err => handleDbError('getActionItemsFromRtDb_DEP onChildAdded', err, e))

    e.handleListener([
      actionItemsRef,
      actionItemsRef2
    ]);
    return;
  }

}


export async function getLiveDataFromDb(e) {
  let liveRef;

  if (e.path) {
    liveRef = fbQuery(
      collectionGroup(db, 'leaderboards'),
      where('path', '==', e.path)
    );
  }

  const unsubscribe = onSnapshot(liveRef, docs => {
    docs.forEach(doc => {
      e.callback(doc.data());
    })
  }, err => handleDbError('getLiveDataFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getLeaderboardDataFromRtDb(e) {

  let leadersRef = rtQuery(
    ref(rtDb, `games/${e.gameId}/playerDocs`),
    orderByChild(e.dataField),
    rtLimitToLast(e.limit || 20)
  );
  get(leadersRef).then(snap => {
    if (snap.exists()) {
      const data = snap.val();
      e.callback(data);
    }
  }).catch(err => handleDbError('getLeaderboardDataFromRtDb', err, e));
  if (e.once) return;
  const nowObj = await UseCloudFunction('getServerTimeStamp', {})
    .catch(err => handleDbError('getLeaderboardDataFromRtDb getServerTimeStamp', err, e));
  if (!nowObj) return;
  const now = nowObj.ts;
  // console.log(now);
  let playerDocsRef2 = rtQuery(
    ref(rtDb, `games/${e.gameId}/playerDocs`),
    orderByChild('ts'),
    startAt(now)
  );
  onChildAdded(playerDocsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.playerId]: data });
  }, err => handleDbError('getLeaderboardDataFromRtDb onChildAdded', err, e))
  onChildChanged(playerDocsRef2, snap => {
    const data = snap.val();
    e.callback({ [data.playerId]: data });
  }, err => handleDbError('getLeaderboardDataFromRtDb onChildChanged', err, e))
  e.handleListener(playerDocsRef2);
}

// DEPRECATED
export async function getLeaderboardDataFromDb_DEP(e) {
  let leaderboardRef;

  if (e.gameId) {
    leaderboardRef = fbQuery(collection(db, 'gamePlayerDocs'), where('gameId', "==", e.gameId));
  }

  const unsubscribe = onSnapshot(leaderboardRef, docs => {
    let playerObj = {}
    docs.forEach(doc => {
      const data = doc.data();
      playerObj = { ...playerObj, ...data.players }

    });
    e.callback(playerObj);
  }, err => handleDbError('getLeaderboardDataFromDb_DEP', err, e));
  e.handleListener(unsubscribe);
}

export async function getUserMembershipFromDb(e) {
  let membershipRef;

  if (e.appId) {
    membershipRef = doc(db, 'memberships', e.appId);
  }

  const unsubscribe = onSnapshot(membershipRef, doc => {
    const data = doc.data();
    e.callback(data)
  }, err => handleDbError('getUserMembershipFromDb', err, e));
  e.handleListener(unsubscribe);

}

export async function getOrgInvitesFromDb(e) {
  let inviteRef;

  if (e.email) {
    inviteRef = fbQuery(collection(db, 'orgInvites'), where('email', '==', e.email));
  }
  const unsubscribe = onSnapshot(inviteRef, docs => {
    docs.forEach(doc => {
      const data = doc.data();
      e.callback(data, doc.id);
    });
    // optional: fires after every snapshot, including an empty one, so a
    // caller can tell "no invites" apart from "not loaded yet"
    if (e.onLoaded) e.onLoaded();
  }, err => handleDbError('getOrgInvitesFromDb', err, e));
  e.handleListener(unsubscribe);
}

export async function getGameInvitesFromDb(e) {
  let inviteRef;

  if (e.inviteAppId) {
    inviteRef = fbQuery(
      collectionGroup(db, 'gameInvites'),
      and(
        where('inviteAppId', '==', e.inviteAppId),
        where('status', '==', 'pending')
      )
    );
  }
  const unsubscribe = onSnapshot(inviteRef, docs => {
    docs.forEach(doc => {
      const data = doc.data();
      e.callback(data, doc.id);
    });
  }, err => handleDbError('getGameInvitesFromDb', err, e));
  e.handleListener(unsubscribe);
}

export async function getOrgDataFromDb(e) {
  let orgRef;
  if (e.orgId && e.status && e.current) {
    orgRef = fbQuery(collection(db, 'orgs'),
      and(
        where('orgId', '==', e.orgId),
        where('status', '==', e.status),
        where('dateExpire', '>=', new Date().getTime())
      )
    );
  } else if (e.orgId) {
    orgRef = fbQuery(collection(db, 'orgs'),
      where('orgId', '==', e.orgId)
    )
  }

  const unsubscribe = onSnapshot(orgRef, docs => {
    docs.forEach(doc => {
      if (!doc.exists) return;
      e.callback(doc.data(), doc.id);
    })
  }, err => handleDbError('getOrgDataFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getUniqueDocumentFromDb(e) {
  let docRef = doc(db, e.collection, e.docId);

  if (e.once) {
    const docSnap = await getDoc(docRef).catch(err => handleDbError('getUniqueDocumentFromDb', err, e));
    if (!docSnap) return;
    if (docSnap.exists()) {
      const data = docSnap.data();
      e.callback(data, docSnap.id);
    } else {
      e.callback(null, null);
    }
    return;
  }

  const unsubscribe = onSnapshot(docRef, doc => {
    const data = doc.data();
    e.callback(data, doc.id)
  }, err => handleDbError('getUniqueDocumentFromDb', err, e));
  e.handleListener(unsubscribe);
}

export async function getCollectionFromDb(e) {
  let collRef;

  if (e.query1 && e.query2 && e.query3) {
    collRef = fbQuery(
      collection(db, e.collection),
      and(
        where(e.query1.key, e.query1.condition, e.query1.value),
        where(e.query2.key, e.query2.condition, e.query2.value),
        where(e.query3.key, e.query3.condition, e.query3.value)
      )
    );
  }
  else if (e.query1 && e.query2) {
    collRef = fbQuery(
      collection(db, e.collection),
      and(
        where(e.query1.key, e.query1.condition, e.query1.value),
        where(e.query2.key, e.query2.condition, e.query2.value)
      )
    );
  }
  else if (e.query1) {
    collRef = fbQuery(
      collection(db, e.collection),
      where(e.query1.key, e.query1.condition, e.query1.value)
    );
  }
  else {
    collRef = collection(db, e.collection);
  }

  if (e.once) {
    const collSnap = await getDocs(collRef).catch(err => handleDbError('getCollectionFromDb', err, e));
    if (!collSnap) return;
    collSnap.forEach(doc => {
      const data = doc.data();
      e.callback(data, doc.id);
    });
    return;
  }

  const unsubscribe = onSnapshot(collRef, docs => {
    // snapshotCallback receives the whole result set at once, keyed by doc id,
    // so the caller can tell when a document has left the query.
    if (e.snapshotCallback) {
      const docsById = {};
      docs.forEach(doc => {
        docsById[doc.id] = doc.data();
      });
      e.snapshotCallback(docsById);
      return;
    }
    docs.forEach(doc => {
      const data = doc.data();
      e.callback(data, doc.id);
    })
  }, err => handleDbError('getCollectionFromDb', err, e))
  // Was inside the snapshot callback, so the caller never received the unsubscribe
  // until the first snapshot landed — and never at all if the listener errored first.
  e.handleListener(unsubscribe);
}





// Hands back every message in the game, keyed by id, on each change so that
// deleted messages drop out of the caller's state.
export async function getGameMessagesFromDb(e) {
  const messagesRef = collection(db, `games/${e.gameId}/messages`);
  const unsubscribe = onSnapshot(messagesRef, docs => {
    const messages = {};
    docs.forEach(doc => {
      messages[doc.id] = doc.data();
    })
    e.callback(messages);
  }, err => handleDbError('getGameMessagesFromDb', err, e))
  e.handleListener(unsubscribe);
}

export async function getLotteryDataFromDb(e) {
  let lotteryRef;

  if (e.gameId && e.elementId) {
    lotteryRef = ref(rtDb, `games/${e.gameId}/lotteryTicketLists/${e.elementId}`);
  } else if (e.gameId) {
    lotteryRef = ref(rtDb, `games/${e.gameId}/lotteryTicketLists`);
  }
  onValue(lotteryRef, snap => {
    const data = snap.val();
    e.callback(data);
  }, err => handleDbError('getLotteryDataFromDb', err, e))
  e.handleListener(lotteryRef);
}

export async function getMyContentFromDb(e) {
  const dataRef = ref(rtDb, `myContent/${e.appId}`);
  if (e.once) {
    get(dataRef).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        e.callback(data);
      } else {
        e.callback(null);
      }
    }).catch(err => handleDbError('getMyContentFromDb', err, e));
    return;
  }
  onChildAdded(dataRef, snap => {
    e.callback(snap.val());
  }, err => handleDbError('getMyContentFromDb onChildAdded', err, e))
  e.handleListener(dataRef);
}

export async function getPackageFromDb(e) {

  let dataRef;
  if (e.packageId) {
    dataRef = ref(rtDb, `myContent_packages/${e.packageId}`);
  } else {
    dataRef = ref(rtDb, `myContent_packages`);
  }
  get(dataRef).then(snap => {
    if (snap.exists()) {
      const data = snap.val();
      e.callback(data);
    }
  }).catch(err => handleDbError('getPackageFromDb', err, e));
}

export async function getLeaderboardChangeDataFromDb(e) {
  let changeRef = ref(rtDb, `games/${e.gameId}/leaderboardChangeData/${e.leaderboardId}`);
  onValue(changeRef, snap => {
    const data = snap.val();
    e.callback(data);
  }, err => handleDbError('getLeaderboardChangeDataFromDb', err, e))
  e.handleListener(changeRef);
}